import type {
  SkynetUserGroupMember,
  SkynetUserGroupPermissions,
} from "../models/RolesAndPermissionsModels";

export const AVAILABLE_READ_PERMISSIONS = {
  InternalAdminUser: "InternalAdminPermissions:User",
  InternalAdminRule: "InternalAdminPermissions:Rule",
  InternalAdminLegal: "InternalAdminPermissions:Legal",
  InternalAdminConfiguration: "InternalAdminPermissions:Configuration",
  InternalAmlRiskScore: "InternalAmlPermissions:AmlRiskScore",
  InternalAmlMonitoring: "InternalAmlPermissions:AmlMonitoring",
  InternalAmlLog: "InternalAmlPermissions:AmlLog",
  InternalAmlExport: "InternalAmlPermissions:AmlExport",
  InternalAmlBlacklist: "InternalAmlPermissions:Blacklist",
  InternalAmlDebtCollection: "InternalAmlPermissions:DebtCollection",
  InternalClientEmailAddressConnection:
    "InternalClientPermissions:EmailAddressConnection",
  InternalClientAccountPlan: "InternalClientPermissions:AccountPlan",
  InternalClientUser: "InternalClientPermissions:User",
  InternalClientSettings: "InternalClientPermissions:Settings",
  InternalClientSettlement: "InternalClientPermissions:Settlement",
  InternalFileSettlement: "InternalFilePermissions:Settlement",
  InternalFileInvoiceDocumentIngress:
    "InternalFilePermissions:InvoiceDocumentIngress",
  InternalInvoiceUploadUnit: "InternalInvoicePermissions:InvoiceUploadUnit",
  InternalInvoiceInterpretation:
    "InternalInvoicePermissions:InvoiceInterpretation",
  InternalOnboardingBank: "InternalOnboardingPermissions:Bank",
  InternalOnboardingAgreement: "InternalOnboardingPermissions:Agreement",
  InternalOnboardingKyc: "InternalOnboardingPermissions:Kyc",
  InternalOnboardingInternalKyc: "InternalOnboardingPermissions:InternalKyc",
  InternalOnboardingSales: "InternalOnboardingPermissions:Sales",
  InternalOnboardingInternalSales:
    "InternalOnboardingPermissions:InternalSales",
} as const;

export type AvailableReadPermission =
  (typeof AVAILABLE_READ_PERMISSIONS)[keyof typeof AVAILABLE_READ_PERMISSIONS];

export const AVAILABLE_WRITE_PERMISSIONS = {
  InternalAdminUserGroup: "InternalAdminPermissions:UserGroup",
} as const;

export const RuleBucketsPermissions = [
  AVAILABLE_READ_PERMISSIONS.InternalAdminRule,
];
export const OnboardingPermissions = [
  AVAILABLE_READ_PERMISSIONS.InternalOnboardingBank,
  AVAILABLE_READ_PERMISSIONS.InternalOnboardingAgreement,
  AVAILABLE_READ_PERMISSIONS.InternalOnboardingKyc,
  AVAILABLE_READ_PERMISSIONS.InternalOnboardingInternalKyc,
];
export const AgreementsPermissions = [
  AVAILABLE_READ_PERMISSIONS.InternalAdminLegal,
];
export const MonitoringPermissions = [
  AVAILABLE_READ_PERMISSIONS.InternalAdminRule,
  AVAILABLE_READ_PERMISSIONS.InternalAdminConfiguration,
  AVAILABLE_READ_PERMISSIONS.InternalAdminUser,
];
export const BlacklistPermissions = [
  AVAILABLE_READ_PERMISSIONS.InternalAmlBlacklist,
];
export const UsersPermissions = [
  AVAILABLE_READ_PERMISSIONS.InternalAdminUser,
  AVAILABLE_READ_PERMISSIONS.InternalAdminConfiguration,
];
export const AmlMonitoringPermissions = [
  AVAILABLE_READ_PERMISSIONS.InternalAmlMonitoring,
  AVAILABLE_READ_PERMISSIONS.InternalAdminUser,
  AVAILABLE_READ_PERMISSIONS.InternalAdminLegal,
];

export const AmlRiskScorePermissions = [
  AVAILABLE_READ_PERMISSIONS.InternalAmlRiskScore,
  AVAILABLE_READ_PERMISSIONS.InternalAdminUser,
  AVAILABLE_READ_PERMISSIONS.InternalAdminLegal,
];

export const SettingsPermissions = [
  AVAILABLE_READ_PERMISSIONS.InternalAdminConfiguration,
];

const OVERRIDE_MODULES: Array<
  keyof NonNullable<SkynetUserGroupMember["overrides"]>
> = ["admin", "aml", "client", "file", "invoice", "onboarding"];

const PERMISSION_MODULES: Array<keyof SkynetUserGroupPermissions> = [
  "admin",
  "aml",
  "client",
  "file",
  "invoice",
  "onboarding",
];

export const stripGroupPermissionsTypenames = (
  permissions: SkynetUserGroupPermissions,
): SkynetUserGroupPermissions => {
  const result: SkynetUserGroupPermissions = {};
  for (const mod of PERMISSION_MODULES) {
    const m = permissions[mod] as any;
    if (m) {
      result[mod] = {
        defaultRead: [...(m.defaultRead ?? [])],
        defaultWrite: [...(m.defaultWrite ?? [])],
        maxRead: [...(m.maxRead ?? [])],
        maxWrite: [...(m.maxWrite ?? [])],
      };
    }
  }
  return result;
};

export const checkUserInGroupHasOverrides = (
  member: SkynetUserGroupMember,
): boolean =>
  OVERRIDE_MODULES.some((mod) => {
    const m = member.overrides?.[mod] as any;
    return m != null && (m.read?.length > 0 || m.write?.length > 0);
  });
